/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { createContext, useContext } from 'react';
import type { WidgetType } from '@utils/widget-registry';
import { WidgetSize } from '@utils/dashboard-widget-context.tsx';

export interface WidgetItem {
    id: string;
    layoutId: string;
    widgetTypeId: string;
    widgetType?: WidgetType;
    widgetSize: WidgetSize;
    gridX: number;
    gridY: number;
    config?: Record<string, unknown>;
    zOrder: number;
    isVisible: boolean;
    isLocked: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface WidgetLayout {
    id: string;
    userId: string;
    name: string;
    description?: string;
    isActive: boolean;
    isDefault: boolean;
    layoutVersion: number;
    items: WidgetItem[];
    createdAt?: string;
    updatedAt?: string;
}

export interface DashboardContextType {
    availableWidgetTypes: WidgetType[];
    loadingWidgetTypes: boolean;
    layout: WidgetLayout | null;
    loading: boolean;
    error: string | null;
    fetchLayout: () => Promise<void>;
    updateWidgetPosition: (widgetId: string, gridX: number, gridY: number) => Promise<void>;
    updateWidget: (widgetId: string, updates: Partial<WidgetItem>) => Promise<void>;
    deleteWidget: (widgetId: string) => Promise<void>;
    addWidget: (widget: Partial<WidgetItem>) => Promise<void>;
    saveLayout: () => Promise<void>;
}

export const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export function useDashboard() {
    const ctx = useContext(DashboardContext);
    if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
    return ctx;
}
